import { apiGet } from './apiClient';

export interface CompanyData {
  company_id: string;
  company_name: string;
  sector: string;
  reliability_score: number;
}

export interface DataPackage {
  id: string;
  name: string;
  description: string;
  companies_data: CompanyData[];
  positions: string[];
  package_name: string | null;
  created_at: string;
  updated_at: string;
  hasAccess?: boolean;
}

export const normalizePackageDescription = (description: string): string =>
  description === 'Пакет данных по пакетам коменсаций в компаниях сферы инвестиций и корпоративных финансов'
    ? 'Пакет данных по компенсациям в компаниях сферы инвестиций и корпоративных финансов'
    : description;

export const getDataPackages = async (): Promise<DataPackage[]> => {
  try {
    const payload = await apiGet<{ data: DataPackage[] }>('/data-packages');
    return payload?.data || [];
  } catch (error) {
    console.error('Error in getDataPackages:', error);
    return [];
  }
};

export const getDataPackageById = async (id: string): Promise<DataPackage | null> => {
  try {
    const payload = await apiGet<{ data: DataPackage | null }>(`/data-packages/${encodeURIComponent(id)}`);
    return payload?.data || null;
  } catch (error) {
    console.error('Error in getDataPackageById:', error);
    return null;
  }
};

// Функция для расчета средней достоверности
export const calculateAverageReliability = (companies: CompanyData[]): number => {
  if (companies.length === 0) return 0;
  
  const totalScore = companies.reduce((sum, company) => sum + company.reliability_score, 0);
  return totalScore / companies.length;
};

// Функция для получения уникальных секторов
export const getUniqueSectors = (companies: CompanyData[]): string[] => {
  const sectors = companies.map(company => company.sector);
  return sectors.reduce((unique: string[], sector: string) => {
    return unique.includes(sector) ? unique : [...unique, sector];
  }, []);
};

// Получить пакеты, доступные текущему пользователю
export const getUserAccessiblePackages = async (): Promise<DataPackage[]> => {
  try {
    const all = await getDataPackages();
    return all.filter((pkg) => pkg.hasAccess === true);
  } catch (error) {
    console.error('Error in getUserAccessiblePackages:', error);
    return [];
  }
};

// Получить все пакеты с флагом hasAccess для текущего пользователя (с сервера)
export const getAllPackagesWithAccessStatus = async (): Promise<(DataPackage & { hasAccess: boolean })[]> => {
  try {
    const allPackages = await getDataPackages();
    return allPackages.map((pkg) => ({
      ...pkg,
      hasAccess: pkg.hasAccess === true,
    }));
  } catch (error) {
    console.error('Error in getAllPackagesWithAccessStatus:', error);
    return [];
  }
};

// Функция для получения уровня достоверности
export const getReliabilityLevel = (score: number): { level: string; color: string; text: string } => {
  if (score <= 0.3) {
    return { level: 'Low', color: '#999999', text: 'Информация требует проверки (Low)' };
  } else if (score <= 0.5) {
    return { level: 'Moderate', color: '#6d9eeb', text: 'Умеренная достоверность (Moderate)' };
  } else if (score <= 0.8) {
    return { level: 'High', color: '#1155cc', text: 'Высокая достоверность (High)' };
  } else {
    return { level: 'Excellent', color: '#073763', text: 'Очень высокая достоверность (Excellent)' };
  }
};
